import { Howl } from "howler";
import isMobile from "is-mobile";
import React from "react";
import { Modal } from "../Modal";
import { VUMeter } from "../VUMeter";
import { LoggerContext } from "@emed-labs/platform-utility-logger-context";

export interface DeviceWizardProps {
	expertMode?: boolean;
	translate: (key: string) => string;
	onWizardComplete?: () => void;
}

enum MIC_INPUT_STATE {
	LISTENING,
	HEARD,
	NOT_HEARD
}

export interface DeviceWizardState {
	currentAudioOutputDevice?: MediaDeviceInfo;
	currentAudioInputDevice?: MediaDeviceInfo;
	currentVideoInputDevice?: MediaDeviceInfo;

	micInputState?: MIC_INPUT_STATE;
	currentScreenIndex: number;

	isPlayingSound?: boolean;
	retryingOutput?: boolean;

	expertMode?: boolean;
}

const MIN_RMS_VALUE = 0.25; // The minimum mic level
const MIC_WAIT_TIME = 10000;

enum WIZARD_SCREEN {
	EXPERT_MODE,
	INTRO,
	HARDWARE_ERROR,
	AUDIO_INPUT_WIZARD,
	AUDIO_OUTPUT_WIZARD,
	FINISHED
}

const LOGGER = LoggerContext.create("ui.video.DeviceWizard");

export class DeviceWizard extends React.Component<DeviceWizardProps, DeviceWizardState> {
	audioInputDevices: MediaDeviceInfo[] = [];
	audioOutputDevices: MediaDeviceInfo[] = [];
	videoInputDevices: MediaDeviceInfo[] = [];

	audioRef = React.createRef<HTMLAudioElement>();

	wizardScreens: WIZARD_SCREEN[] = [];
	allowVideoInputSelection: boolean = false;
	allowSoundOutputSelection: boolean = false;

	chimeSound = new Howl({
		src: ["/static/sounds/chat_chime.mp3"]
	});

	constructor(props) {
		super(props);

		if (props.expertMode) {
			this.wizardScreens = [WIZARD_SCREEN.EXPERT_MODE];
		} else {
			this.wizardScreens = [WIZARD_SCREEN.INTRO];
		}

		this.state = {
			currentScreenIndex: 0,
			expertMode: props.expertMode
		};
	}

	private static async _getDeviceController(): Promise<{
		listAudioInputDevices: () => any;
		listAudioOutputDevices: () => any;
		listVideoInputDevices: () => any;
	}> {
		let devices: MediaDeviceInfo[] = [];
		try {
			devices = await navigator.mediaDevices.enumerateDevices();
		} catch (error) {
			LOGGER.error("_getDeviceController", "enumerateDevices error", error);
		}

		return {
			listAudioInputDevices: () => devices.filter((device) => device.kind === "audioinput"),
			listAudioOutputDevices: () => devices.filter((device) => device.kind === "audiooutput"),
			listVideoInputDevices: () => devices.filter((device) => device.kind === "videoinput")
		};
	}

	private static _getSavedDevice(keyName: string, devices: MediaDeviceInfo[]): MediaDeviceInfo {
		if (!devices || devices.length === 0) {
			return;
		}

		const savedDeviceID = localStorage.getItem(keyName);
		let returnDevice;

		if (savedDeviceID) {
			returnDevice = devices.find((device) => device.deviceId === savedDeviceID);
		}

		if (!returnDevice) {
			returnDevice = devices[0];
		}

		return returnDevice;
	}

	static needsSetup(): boolean {
		return !localStorage.getItem("__avSetup");
	}

	// Does this machine even have devices we can setup?
	static async hasSetup(): Promise<boolean> {
		if (isMobile({ tablet: true })) {
			return false;
		}

		if ((await DeviceWizard._getDeviceController()).listAudioInputDevices().length > 0) {
			return true;
		}

		if ((await DeviceWizard._getDeviceController()).listAudioOutputDevices().length > 0) {
			return true;
		}

		if ((await DeviceWizard._getDeviceController()).listVideoInputDevices().length > 0) {
			return true;
		}

		return false;
	}

	static async popup(expertMode: boolean = false, translate: (key: string) => string): Promise<boolean> {
		return new Promise((resolve, reject) => {
			Modal.popup(
				<DeviceWizard
					expertMode={expertMode}
					translate={translate}
					onWizardComplete={() => {
						Modal.hide();
						resolve(true);
					}}
				/>,
				"Device Setup",
				false
			);
		});
	}

	static async getCurrentAudioInputDevice(): Promise<MediaDeviceInfo | undefined> {
		try {
			const dev = (await DeviceWizard._getDeviceController()).listAudioInputDevices();
			return DeviceWizard._getSavedDevice("__audioInputDevice", dev);
		} catch (e) {
			LOGGER.error("getCurrentAudioInputDevice", "error", e);
			return undefined;
		}
	}

	static async getCurrentAudioOutputDevice(): Promise<MediaDeviceInfo | undefined> {
		try {
			const dev = (await DeviceWizard._getDeviceController()).listAudioOutputDevices();
			return DeviceWizard._getSavedDevice("__audioOutputDevice", dev);
		} catch (e) {
			LOGGER.error("getCurrentAudioOutputDevice", "error", e);
			return undefined;
		}
	}

	static async getCurrentVideoInputDevice(): Promise<MediaDeviceInfo | undefined> {
		try {
			const dev = (await DeviceWizard._getDeviceController()).listVideoInputDevices();
			return DeviceWizard._getSavedDevice("__videoInputDevice", dev);
		} catch (e) {
			LOGGER.error("getCurrentVideoInputDevice", "error", e);
			return undefined;
		}
	}

	toggleSound = () => {
		this.playSound(!this.state.isPlayingSound);
	};

	async playSound(play?: boolean) {
		if (play) {
			try {
				this.chimeSound.play();
				this.setState({
					isPlayingSound: true
				});
			} catch (err) {
				LOGGER.error("playSound", "Unable to play sound", err);
			}
		} else {
			try {
				this.chimeSound.stop();
				this.setState({
					isPlayingSound: false
				});
			} catch (err) {
				LOGGER.error("playSound", "Unable to stop sound", err);
			}
		}
	}

	handleWizardFinished = () => {
		this.state.currentAudioInputDevice?.deviceId
			? localStorage.setItem("__audioInputDevice", this.state.currentAudioInputDevice?.deviceId)
			: localStorage.removeItem("__audioInputDevice");
		this.state.currentAudioOutputDevice?.deviceId
			? localStorage.setItem("__audioOutputDevice", this.state.currentAudioOutputDevice?.deviceId)
			: localStorage.removeItem("__audioOutputDevice");
		this.state.currentVideoInputDevice?.deviceId
			? localStorage.setItem("__videoInputDevice", this.state.currentVideoInputDevice?.deviceId)
			: "__videoInputDevice";
		localStorage.setItem("__avSetup", "done");

		if (this.props.onWizardComplete) {
			this.props.onWizardComplete();
		}
	};

	// This function switches the microphone input to try and find one that we can hear.
	micInputTimeout;
	handleWaitForMic = () => {
		if (this.micInputTimeout || this.state.micInputState !== MIC_INPUT_STATE.LISTENING) {
			return;
		}

		this.micInputTimeout = setTimeout(() => {
			this.micInputTimeout = null;

			if (this.state.micInputState !== MIC_INPUT_STATE.LISTENING) {
				return;
			}

			this.setState({
				micInputState: MIC_INPUT_STATE.NOT_HEARD
			});
		}, MIC_WAIT_TIME);
	};

	handleMoveNextMic = () => {
		let currentDeviceIndex = this.audioInputDevices.findIndex(
			(device) => device.deviceId === this.state.currentAudioInputDevice.deviceId
		);

		// Move to the next device
		currentDeviceIndex++;

		if (currentDeviceIndex >= this.audioInputDevices.length) {
			currentDeviceIndex = 0;
		}

		this.setState({
			micInputState: MIC_INPUT_STATE.LISTENING,
			currentAudioInputDevice: this.audioInputDevices[currentDeviceIndex]
		});
	};

	handleMoveNextSpeaker = () => {
		let currentDeviceIndex = this.audioOutputDevices.findIndex(
			(device) => device.deviceId === this.state.currentAudioOutputDevice.deviceId
		);

		// Move to the next device
		currentDeviceIndex++;

		if (currentDeviceIndex >= this.audioOutputDevices.length) {
			currentDeviceIndex = 0;
		}

		this.setState({
			currentAudioOutputDevice: this.audioOutputDevices[currentDeviceIndex],
			retryingOutput: true
		});
	};

	handleWizardMoveNext = () => {
		this.setState({
			currentScreenIndex: this.state.currentScreenIndex + 1
		});
	};

	componentWillUnmount() {
		this.playSound(false);

		if (this.micInputTimeout) {
			clearTimeout(this.micInputTimeout);
		}
	}

	async componentDidUpdate(
		prevProps: Readonly<DeviceWizardProps>,
		prevState: Readonly<DeviceWizardState>,
		snapshot?: any
	) {
		const currentScreen = this.wizardScreens[this.state.currentScreenIndex];

		if (this.state.isPlayingSound && this.state.currentAudioOutputDevice !== prevState.currentAudioOutputDevice) {
			await this.playSound(false);
			await this.playSound(true);
		}

		if (this.state.currentScreenIndex !== prevState.currentScreenIndex) {
			if (currentScreen === WIZARD_SCREEN.AUDIO_OUTPUT_WIZARD) {
				await this.playSound(true);
			} else {
				await this.playSound(false);
			}
		}

		if (currentScreen === WIZARD_SCREEN.AUDIO_INPUT_WIZARD) {
			this.handleWaitForMic();
		}
	}

	async componentDidMount() {
		this.wizardScreens = [];
		// Make sure we ask for user permission
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
			stream.getTracks().map((track) => track.stop());
		} catch (e) {
			LOGGER.error("componentDidMount", "getUserMedia error", e);
			this.wizardScreens.push(WIZARD_SCREEN.HARDWARE_ERROR);
		}

		// We check to see if we can choose a sound output device
		//@ts-ignore
		this.allowSoundOutputSelection = !!new Audio()?.setSinkId;
		this.allowVideoInputSelection = !isMobile({ tablet: true });

		this.audioInputDevices = (await DeviceWizard._getDeviceController()).listAudioInputDevices();
		this.audioOutputDevices = (await DeviceWizard._getDeviceController()).listAudioOutputDevices();
		this.videoInputDevices = (await DeviceWizard._getDeviceController()).listVideoInputDevices();

		if (!this.audioInputDevices || !this.audioOutputDevices) {
			this.wizardScreens.push(WIZARD_SCREEN.HARDWARE_ERROR);
		}

		const newState: Partial<DeviceWizardState> = {
			currentScreenIndex: 0,
			micInputState: MIC_INPUT_STATE.LISTENING,
			currentAudioInputDevice: await DeviceWizard.getCurrentAudioInputDevice(),
			currentAudioOutputDevice: await DeviceWizard.getCurrentAudioOutputDevice(),
			currentVideoInputDevice: await DeviceWizard.getCurrentVideoInputDevice()
		};

		if (this.state.expertMode) {
			this.wizardScreens.push(WIZARD_SCREEN.EXPERT_MODE);
		} else {
			// If we can't listen for audio mic levels then skip this screen.
			if (window.AudioContext || (window as any).webkitAudioContext) {
				this.wizardScreens.push(WIZARD_SCREEN.AUDIO_INPUT_WIZARD);
			}

			if (this.allowSoundOutputSelection) {
				this.wizardScreens.push(WIZARD_SCREEN.AUDIO_OUTPUT_WIZARD);
			}

			if (this.wizardScreens.length === 0) {
				// If there are no wizard screens, then we can consider this wizard finished
				this.handleWizardFinished();
				return;
			} else {
				this.wizardScreens = [WIZARD_SCREEN.INTRO, ...this.wizardScreens, WIZARD_SCREEN.FINISHED];
			}
		}

		this.setState(newState as DeviceWizardState);
	}

	switchToExpertMode = () => {
		this.wizardScreens = [WIZARD_SCREEN.EXPERT_MODE];
		this.setState({
			currentScreenIndex: 0
		});
	};

	handleVUChange = (rms: number) => {
		if (rms >= MIN_RMS_VALUE) {
			this.setState({
				micInputState: MIC_INPUT_STATE.HEARD
			});
		}
	};

	renderManualSettingsButton() {
		return (
			<p className="buttons is-centered">
				<button className="button is-small is-text" onClick={this.switchToExpertMode}>
					{this.props.translate("manual-settings")}
				</button>
			</p>
		);
	}

	renderSelectAudioInput() {
		switch (this.state.micInputState) {
			case MIC_INPUT_STATE.LISTENING: {
				return (
					<div className="content">
						<h2>{this.props.translate("check-mic")}...</h2>
						<p>{this.props.translate("say-mic")}</p>
						<p>
							<VUMeter
								audioInputDeviceID={this.state.currentAudioInputDevice?.deviceId}
								onAudioLevelChange={this.handleVUChange}
							/>
						</p>
						{this.state.currentAudioInputDevice?.label ? (
							<p className="help">
								{this.props.translate("listening-on")} {this.state.currentAudioInputDevice?.label}...
							</p>
						) : null}
						{this.renderManualSettingsButton()}
					</div>
				);
			}
			case MIC_INPUT_STATE.HEARD: {
				return (
					<div className="content">
						<h2>{this.props.translate("hear-you")}</h2>
						<p className="buttons is-centered">
							<button className="button is-dark" onClick={this.handleWizardMoveNext}>
								{this.props.translate("next")}
							</button>
						</p>
					</div>
				);
			}
			case MIC_INPUT_STATE.NOT_HEARD: {
				return (
					<div className="content">
						<h2>{this.props.translate("could-not-hear")}</h2>
						<p>{this.props.translate("different-device")}</p>
						<p className="buttons is-centered">
							<button className="button is-dark" onClick={this.handleMoveNextMic}>
								{this.props.translate("try-again")}
							</button>
						</p>
						{this.renderManualSettingsButton()}
					</div>
				);
			}
		}

		return null;
	}

	renderSelectAudioOutput() {
		return (
			<div className="content">
				<h2>{this.props.translate("readiness-checker.hear-sound")}</h2>
				<audio src="/static/sounds/chat_chime.mp3" ref={this.audioRef} style={{ visibility: "hidden" }} loop />
				{this.state.retryingOutput ? <p>{this.props.translate("how-about-now")}</p> : null}
				{this.state.currentAudioOutputDevice?.label ? (
					<p className="help">
						{this.props.translate("playing_on")} {this.state.currentAudioOutputDevice?.label}...
					</p>
				) : null}

				<p className="buttons is-centered">
					<button className="button is-text" onClick={this.handleMoveNextSpeaker}>
						{this.props.translate("no")}
					</button>
					<button className="button is-dark" onClick={this.handleWizardMoveNext}>
						{this.props.translate("yes")}
					</button>
				</p>
				{this.renderManualSettingsButton()}
			</div>
		);
	}

	renderAllSet() {
		return (
			<div className="content">
				<h2>{this.props.translate("all-set")}</h2>
				<p>{this.props.translate("save-settings")}</p>
				<p className="buttons is-centered">
					<button className="button is-dark" onClick={this.handleWizardFinished}>
						{this.props.translate("finish")}
					</button>
				</p>
				{this.renderManualSettingsButton()}
			</div>
		);
	}

	renderIntro() {
		return (
			<div className="content">
				<h2>{this.props.translate("see-hear")}</h2>
				<p>{this.props.translate("start-device-setup")}</p>
				<p className="buttons is-centered">
					<button className="button is-dark" onClick={this.handleWizardMoveNext}>
						{this.props.translate("start")}
					</button>
				</p>
				{this.renderManualSettingsButton()}
			</div>
		);
	}

	renderHardwareError() {
		return (
			<div className="content">
				<h2>Hardware Error</h2>
				<p>There was an error accessing your audio/video. Permission may have been denied.</p>
				<p>Please reload this page and grant permissions.</p>
			</div>
		);
	}

	renderExpertMode() {
		return (
			<div>
				<div className="field">
					<label className="label">{this.props.translate("microphone")}</label>
					{!this.state.currentAudioInputDevice?.deviceId && (
						<p>WARNING: Unable to find microphone. Permissions may not have been granted.</p>
					)}
					<div className="control is-expanded">
						<div className="select is-fullwidth">
							<select
								value={this.state.currentAudioInputDevice?.deviceId}
								onChange={(event) =>
									this.setState({
										currentAudioInputDevice: this.audioInputDevices.find(
											(device) => event.target.value === device.deviceId
										)
									})
								}
							>
								{this.audioInputDevices.map((device) => (
									<option key={device.deviceId} value={device.deviceId}>
										{device.label}
									</option>
								))}
							</select>
						</div>
					</div>
					<VUMeter audioInputDeviceID={this.state.currentAudioInputDevice?.deviceId} />
				</div>
				{this.allowSoundOutputSelection ? (
					<div className="field">
						<label className="label">{this.props.translate("speaker")}</label>
						{!this.state.currentAudioOutputDevice?.deviceId && (
							<p>WARNING: Unable to connect to speaker. Permissions may not have been granted.</p>
						)}
						<div className="control is-expanded">
							<div className="select is-fullwidth">
								<select
									value={this.state.currentAudioOutputDevice?.deviceId}
									onChange={(event) =>
										this.setState({
											currentAudioOutputDevice: this.audioOutputDevices.find(
												(device) => event.target.value === device.deviceId
											)
										})
									}
								>
									{this.audioOutputDevices.map((device) => (
										<option key={device.deviceId} value={device.deviceId}>
											{device.label}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className="buttons is-centered">
							<button className="button is-dark is-small" onClick={this.toggleSound}>
								{this.state.isPlayingSound ? this.props.translate("stop-sound") : this.props.translate("tests-sound")}
							</button>
						</div>
					</div>
				) : null}
				{this.allowVideoInputSelection ? (
					<div className="field">
						<label className="label">{this.props.translate("camera")}</label>
						{!this.state.currentVideoInputDevice?.deviceId && (
							<p>WARNING: Unable to connect to camera. Permissions may not have been granted.</p>
						)}
						<div className="control is-expanded">
							<div className="select is-fullwidth">
								<select
									value={this.state.currentVideoInputDevice?.deviceId}
									onChange={(event) =>
										this.setState({
											currentVideoInputDevice: this.videoInputDevices.find(
												(device) => event.target.value === device.deviceId
											)
										})
									}
								>
									{this.videoInputDevices.map((device) => (
										<option key={device.deviceId} value={device.deviceId}>
											{device.label}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				) : null}
				<p className="buttons is-centered">
					<button className="button is-dark" onClick={this.handleWizardFinished}>
						{this.props.translate("finish")}
					</button>
				</p>
			</div>
		);
	}

	render() {
		if (this.wizardScreens.length > 0) {
			switch (this.wizardScreens[this.state.currentScreenIndex]) {
				case WIZARD_SCREEN.EXPERT_MODE: {
					return this.renderExpertMode();
				}
				case WIZARD_SCREEN.INTRO: {
					return this.renderIntro();
				}
				case WIZARD_SCREEN.HARDWARE_ERROR: {
					return this.renderHardwareError();
				}
				case WIZARD_SCREEN.AUDIO_OUTPUT_WIZARD: {
					return this.renderSelectAudioOutput();
				}
				case WIZARD_SCREEN.AUDIO_INPUT_WIZARD: {
					return this.renderSelectAudioInput();
				}
				case WIZARD_SCREEN.FINISHED: {
					return this.renderAllSet();
				}
			}
		}

		return null;
	}
}
